import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { SITE } from "@/lib/site";
import { hreflangLinks } from "@/lib/hreflang";
import {
  pageSchemaScript,
  PERSON_ID,
  PORTRAIT_ID,
  SITE_URL,
  PRIMARY_PORTRAIT_PATH,
  PRIMARY_PORTRAIT_WIDTH,
  PRIMARY_PORTRAIT_HEIGHT,
} from "@/seo/schema";

const PATH = "/perimeter-security-expert-in-india";
const URL = `${SITE_URL}${PATH}`;
const TITLE = "Perimeter Security Expert in India | Arif Khan";
const DESC =
  "Arif Khan plans perimeter intrusion detection in India: electric fence, fibre PIDS, LiDAR, infrared and thermal, verified by CCTV and tied to response.";

const FAQS = [
  {
    q: "What is a perimeter intrusion detection system?",
    a: "A perimeter intrusion detection system (PIDS) uses sensors along a fence, wall or open boundary to raise an alarm when someone tries to cross, climb or cut it. The alarm identifies a zone so cameras and guards can verify and respond.",
  },
  {
    q: "Which perimeter security system is suitable for a large industrial site?",
    a: "There is no single answer. The choice depends on boundary length and construction, terrain, vegetation, threat level and how the site will respond. Large industrial sites often combine a detection layer such as fibre PIDS or electric fencing with thermal or PTZ camera verification.",
  },
  {
    q: "What is the difference between electric fencing and perimeter intrusion detection?",
    a: "An electric fence is a physical deterrent that can also raise an alarm when it is cut, shorted or tampered with. Perimeter intrusion detection is the broader category of sensors, such as fibre, LiDAR, infrared or thermal, used to detect a crossing. Electric fencing is one possible detection method, not the whole system.",
  },
  {
    q: "Can CCTV alone provide perimeter intrusion detection?",
    a: "Generally not reliably. CCTV is best used to verify an alarm. Relying on operators or video analytics alone to spot intrusion along a long boundary tends to miss events or produce too many alerts; a dedicated detection layer is usually needed.",
  },
  {
    q: "When should thermal cameras be used for perimeter protection?",
    a: "Thermal cameras are useful where visible-light cameras struggle, such as at night, over large open areas or where boundary lighting is limited, because they detect heat from people and vehicles rather than relying on light.",
  },
  {
    q: "What is LiDAR perimeter detection?",
    a: "LiDAR perimeter detection uses laser scanning to create invisible detection zones along a boundary, roof edge or open area. It does not need to be mounted on the fence itself, which makes it useful where contact-based sensors are unsuitable.",
  },
  {
    q: "How can false or nuisance alarms be reduced in perimeter security systems?",
    a: "By surveying the site before choosing technology, designing out vegetation, animals and weather effects, using sensible zone lengths, calibrating sensors on site and verifying every alarm with cameras before dispatching a response.",
  },
  {
    q: "When should perimeter security design begin in a new project?",
    a: "Before civil, fencing, lighting, conduit and control-room decisions are frozen. Planning early lets power, fibre routes, camera poles and fence details be built in rather than retrofitted.",
  },
];

export const Route = createFileRoute("/perimeter-security-expert-in-india")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(PATH)],
    scripts: [
      pageSchemaScript(
        {
          path: PATH,
          type: "WebPage",
          name: TITLE,
          description: DESC,
          breadcrumb: [{ name: "Perimeter Security Expert in India" }],
          extra: {
            about: [{ "@id": PERSON_ID }, { "@id": `${URL}#service` }],
            mainEntity: { "@id": PERSON_ID },
            primaryImageOfPage: { "@id": PORTRAIT_ID },
          },
        },
        [
          {
            "@type": "Service",
            "@id": `${URL}#service`,
            name: "Perimeter security and intrusion detection planning",
            serviceType: "Perimeter security consulting",
            provider: { "@id": PERSON_ID },
            areaServed: { "@type": "Country", name: "India" },
            url: URL,
          },
          {
            "@type": "FAQPage",
            "@id": `${URL}#faq`,
            isPartOf: { "@id": `${URL}#webpage` },
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      ),
    ],
  }),
});

const PROOF = [
  { value: "23+ Years", label: "Security & building technology experience" },
  { value: "1,000+ Projects", label: "Projects" },
  { value: "500 km+", label: "Perimeter security experience" },
];
const PROOF_LINE = "23+ Years · 1,000+ Projects · 500 km+ Perimeter Security";

const SEQUENCE = [
  { t: "Deter", b: "Visible barriers, gates and lighting discourage an attempt." },
  { t: "Detect", b: "Sensors raise an alarm at the boundary, in a known zone." },
  { t: "Verify", b: "Cameras show operators what caused the alarm." },
  { t: "Respond", b: "A defined procedure sends the right people in time." },
];

const WHEN = [
  "Large, long or difficult site boundaries",
  "Industrial plants and warehouses",
  "Airports, transport and infrastructure facilities",
  "Government and institutional environments",
  "Utilities and critical infrastructure",
  "Campuses and institutions",
  "High-value private estates, villas and farmhouses",
  "Sites with isolated or poorly visible boundary sections",
];

const LAYERS = [
  { t: "Physical deterrence", b: "Fence, wall, controlled gates and suitable physical barriers." },
  { t: "Intrusion detection", b: "Sensors chosen for the terrain, threat, boundary construction and environment." },
  { t: "Visual verification", b: "Fixed CCTV, thermal or PTZ cameras that confirm what triggered the alarm." },
  { t: "Response", b: "Alarm handling, control-room procedures, notifications and guard response." },
];

const TECH = [
  { t: "Electric fence", b: "Combines a visible deterrent with an alarm when the fence is cut, shorted or tampered with. Suited to boundaries where a strong visible message matters and the fence line is well defined.", link: { kind: "blog", slug: "electric-fence-installation-delhi-ncr", label: "Electric fence installation" } },
  { t: "Fibre perimeter intrusion detection", b: "A sensing fibre along the fence or buried along the boundary picks up disturbance over long sections. Correct calibration and zoning decide whether alarms are accurate and easy to locate.", link: { kind: "ask", slug: "perimeter-intrusion-detection-system-india", label: "Perimeter intrusion detection in India" } },
  { t: "LiDAR / laser detection", b: "Laser scanning creates monitored detection zones along a boundary, roof edge or open area, without needing to touch the fence.", link: null },
  { t: "Infrared detection", b: "Active beams or passive sensors detect a crossing between points. Alignment, weather, vegetation and site geometry must be planned around.", link: null },
  { t: "Thermal detection", b: "Thermal cameras detect people and vehicles by heat, helping at night or over open ground where visible-light CCTV struggles.", link: { kind: "sol", slug: "cctv-command-centre", label: "CCTV & Command Centre" } },
  { t: "CCTV + PTZ verification", b: "Cameras should confirm an alarm and follow the event, not act as the only detection layer.", link: { kind: "ask", slug: "cctv-planning-guide-for-architects", label: "CCTV planning guide for architects" } },
] as const;

const FACTORS = [
  ["Boundary type", "Fence, wall, open ground or roof edge changes which sensors can be mounted and where."],
  ["Terrain & perimeter length", "Slopes, corners and long runs affect zoning, cabling and camera coverage."],
  ["Threat level", "Who might attempt entry, and how, sets the detection and delay required."],
  ["Environment & nuisance alarms", "Vegetation, animals, wind, rain, fog and nearby traffic must be designed out."],
  ["Visible deterrence", "Some sites need a strong visible barrier; others prefer discreet detection."],
  ["Verification & zoning", "Every zone needs a camera view good enough to confirm what happened."],
  ["Control-room integration", "Alarms, video and response procedures should appear in one place."],
  ["Maintenance", "Access, calibration and upkeep should be realistic for the site team."],
];

const MISTAKES = [
  "Selecting technology before surveying the boundary.",
  "Relying only on CCTV for intrusion detection.",
  "Designing the fence separately from the detection system.",
  "Ignoring vegetation, terrain, animals, weather or nearby movement.",
  "Poor zoning that makes alarms difficult to locate.",
  "Inadequate lighting or camera positioning for verification.",
  "No clear response procedure after an alarm.",
  "Missing provisions for power, network, fibre or communication.",
  "No integration between sensors, cameras and the control room.",
];

const STAGES = [
  { n: "01", t: "Assess", b: "Site survey, boundary condition, threat, terrain, operating environment and response requirements." },
  { n: "02", t: "Design", b: "Zones, detection approach, verification, infrastructure and integration." },
  { n: "03", t: "Coordinate", b: "With architects, civil, electrical/MEP, IT/network teams and security stakeholders." },
  { n: "04", t: "Implement & verify", b: "Installation coordination, testing, commissioning, calibration and practical alarm checks." },
];

const APPLICATIONS = [
  { t: "Critical infrastructure", b: "Long, often remote boundaries where alarms must be accurate enough for operators to trust.", slug: "government-infrastructure" },
  { t: "Industrial plants", b: "Mixed boundaries, vehicle movement and vegetation that create nuisance-alarm risk.", slug: "industrial-facilities" },
  { t: "Airports & transportation", b: "Extensive open perimeters where detection and verification must cover large distances.", slug: null },
  { t: "Government & institutional campuses", b: "Multiple gates and public-facing edges that need controlled entry and clear response.", slug: "government-infrastructure" },
  { t: "Warehouses & logistics", b: "Yards, docks and fence lines that are busy by day and isolated at night.", slug: "warehouses-logistics" },
  { t: "Corporate campuses", b: "Discreet protection that respects landscape and brand while still detecting intrusion.", slug: "corporate-offices" },
  { t: "Luxury villas, farmhouses & private estates", b: "Large grounds and garden edges where security must stay unobtrusive.", slug: "farmhouses-private-estates" },
] as const;

const COORD = ["Fence / wall interface", "Gate locations", "Power", "Conduit", "Fibre / network paths", "Camera poles", "Lighting", "Equipment cabinets", "Control-room integration", "Civil provisions"];

function Section({ id, title, children, alt }: { id: string; title: string; children: React.ReactNode; alt?: boolean }) {
  return (
    <section aria-labelledby={id} className={`py-20 md:py-24 ${alt ? "border-y border-border bg-surface" : ""}`}>
      <div className="container-page">
        <h2 id={id} className="text-3xl md:text-4xl font-semibold tracking-tight max-w-3xl">{title}</h2>
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

function TechLink({ link }: { link: (typeof TECH)[number]["link"] }) {
  if (!link) return null;
  const cls = "mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary";
  const inner = <>{link.label} <ArrowRight className="h-4 w-4" aria-hidden /></>;
  if (link.kind === "blog") return <Link to="/blog/$slug" params={{ slug: link.slug }} className={cls}>{inner}</Link>;
  if (link.kind === "ask") return <Link to="/ask-arif/$slug" params={{ slug: link.slug }} className={cls}>{inner}</Link>;
  return <Link to="/solutions/$slug" params={{ slug: link.slug }} className={cls}>{inner}</Link>;
}

function Page() {
  return (
    <PageShell>
      <section className="relative pt-40 pb-20 border-b border-border bg-surface">
        <div className="container-page grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow">Perimeter Protection · India</span>
            <h1 className="mt-5 text-balance text-5xl md:text-6xl font-semibold tracking-tight leading-[0.95]">
              Perimeter Security Expert in India
            </h1>
            <p className="mt-6 text-lg text-ink-soft leading-relaxed max-w-2xl">
              Arif Khan has 23+ years of experience in electronic security and perimeter protection, planning how complex sites detect, verify and respond to an intrusion before anyone reaches the protected building or asset. His perimeter work covers electric fencing, fibre-based intrusion detection, LiDAR/laser and infrared detection, thermal surveillance, CCTV verification and integrated alarm response.
            </p>
            <p className="mt-3 text-sm text-ink-soft">BU Head, Security &amp; Automation, Altima Global · Projects across India</p>
            <p className="mt-2 text-sm font-medium">{PROOF_LINE}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">
                Discuss a Perimeter Security Project <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link to="/projects" className="tap-target inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium">
                View Selected Projects
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src={PRIMARY_PORTRAIT_PATH}
              alt="Arif Khan, perimeter security and intrusion detection specialist, BU Head, Security & Automation at Altima Global"
              width={PRIMARY_PORTRAIT_WIDTH}
              height={PRIMARY_PORTRAIT_HEIGHT}
              loading="eager"
              decoding="async"
              className="w-full aspect-[4/5] object-cover object-[62%_30%] rounded-3xl border border-border"
            />
          </div>
        </div>
      </section>

      <Section id="credibility" title="Experience at a glance">
        <dl className="grid gap-6 sm:grid-cols-3">
          {PROOF.map((s) => (
            <div key={s.value} className="rounded-2xl border border-border p-6">
              <dd className="text-4xl font-semibold tracking-tight">{s.value}</dd>
              <dt className="mt-2 text-sm text-ink-soft">{s.label}</dt>
            </div>
          ))}
        </dl>
      </Section>

      <Section id="meaning" title="What perimeter security actually means" alt>
        <p className="max-w-3xl text-lg text-ink-soft">Perimeter security is not simply a fence or a camera. Its purpose is to detect a possible intrusion early enough to see what is happening and give the security team time to respond.</p>
        <ol className="mt-10 grid gap-4 md:grid-cols-4">
          {SEQUENCE.map((s, i) => (
            <li key={s.t} className="relative rounded-2xl border border-border bg-background p-6">
              <span className="eyebrow">Step {i + 1}</span>
              <h3 className="mt-2 text-xl font-semibold">{s.t}</h3>
              <p className="mt-2 text-ink-soft">{s.b}</p>
              {i < SEQUENCE.length - 1 && (
                <ArrowRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary bg-surface" aria-hidden />
              )}
            </li>
          ))}
        </ol>
      </Section>

      <Section id="when" title="When a project needs specialist perimeter planning">
        <ul className="grid gap-3 sm:grid-cols-2 max-w-4xl list-disc pl-5 text-ink-soft">
          {WHEN.map((w) => <li key={w}>{w}</li>)}
        </ul>
        <p className="mt-6 max-w-3xl text-ink-soft">Perimeter design works best before civil, fencing, lighting, conduit and control-room decisions are frozen.</p>
      </Section>

      <Section id="layers" title="A layered perimeter security approach" alt>
        <p className="max-w-3xl text-lg text-ink-soft">No single sensor is right for every boundary. Each layer does a different job, and the design starts from risk and site conditions, then technology.</p>
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {LAYERS.map((l, i) => (
            <div key={l.t} className="rounded-2xl border border-border bg-background p-6">
              <span className="eyebrow">Layer {i + 1}</span>
              <h3 className="mt-2 text-lg font-semibold">{l.t}</h3>
              <p className="mt-3 text-ink-soft">{l.b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="tech" title="Perimeter detection technologies">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {TECH.map((t) => (
            <div key={t.t} className="rounded-2xl border border-border p-6">
              <h3 className="text-lg font-semibold">{t.t}</h3>
              <p className="mt-3 text-ink-soft">{t.b}</p>
              <TechLink link={t.link} />
            </div>
          ))}
        </div>
        <p className="mt-6 max-w-3xl text-sm text-ink-soft">No technology is best everywhere. Each has strengths and limits that depend on the site.</p>
      </Section>

      <Section id="choose" title="How to choose between the technologies" alt>
        <p className="max-w-3xl text-lg text-ink-soft">For architects, consultants, facility heads and security decision-makers, these factors shape the choice:</p>
        <dl className="mt-8 grid gap-4 md:grid-cols-2 max-w-5xl">
          {FACTORS.map(([k, v]) => (
            <div key={k} className="rounded-2xl border border-border bg-background p-5">
              <dt className="font-semibold">{k}</dt>
              <dd className="mt-1 text-ink-soft">{v}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-8 max-w-3xl font-medium">Technology selection should follow a site-specific risk and boundary assessment. Complex sites often need more than one detection method.</p>
      </Section>

      <Section id="mistakes" title="Common perimeter security design mistakes">
        <ul className="grid gap-3 md:grid-cols-2 max-w-5xl">
          {MISTAKES.map((m) => (
            <li key={m} className="rounded-xl border border-border p-4 text-ink-soft">{m}</li>
          ))}
        </ul>
      </Section>

      <Section id="method" title="Arif Khan's perimeter security design method" alt>
        <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {STAGES.map((s) => (
            <li key={s.n} className="rounded-2xl border border-border bg-background p-6">
              <span className="eyebrow">{s.n}</span>
              <h3 className="mt-2 text-lg font-semibold">{s.t}</h3>
              <p className="mt-3 text-ink-soft">{s.b}</p>
            </li>
          ))}
        </ol>
        <p className="mt-6 max-w-3xl text-ink-soft">Arif Khan leads the security planning and system-design approach and works with the Altima Global integration team through implementation, testing and commissioning. Depending on the project, his involvement may cover consulting, design, coordination and implementation oversight.</p>
      </Section>

      <Section id="applications" title="Applications by project type">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {APPLICATIONS.map((a) => (
            <div key={a.t} className="rounded-2xl border border-border p-6">
              <h3 className="text-lg font-semibold">{a.t}</h3>
              <p className="mt-3 text-ink-soft">{a.b}</p>
              {a.slug && (
                <Link to="/industries/$slug" params={{ slug: a.slug }} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  Sector page <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section id="work" title="Selected perimeter experience" alt>
        {/* [PROOF NEEDED] INS Rajali Naval Air Station (15 km perimeter): omitted until project name, distance, scope and permission to reference are confirmed. */}
        {/* [PROOF NEEDED] Any other named perimeter project: none published until verified. */}
        <div className="grid gap-6 md:grid-cols-2 max-w-4xl">
          <div className="rounded-2xl border border-border bg-background p-6">
            <span className="eyebrow">Private estates · Delhi NCR</span>
            <h3 className="mt-2 text-lg font-semibold">Residences &amp; farmhouses for HNI clients</h3>
            <p className="mt-3 text-ink-soft">Estate security including perimeter protection, planned alongside the architecture and landscape.</p>
          </div>
          <div className="rounded-2xl border border-border bg-background p-6">
            <span className="eyebrow">More work</span>
            <h3 className="mt-2 text-lg font-semibold">Projects portfolio</h3>
            <p className="mt-3 text-ink-soft">See engagements across security, automation and AV.</p>
            <Link to="/projects" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">View projects <ArrowRight className="h-4 w-4" aria-hidden /></Link>
          </div>
        </div>
      </Section>

      <Section id="architects" title="For architects, MEP consultants & project teams">
        <p className="max-w-3xl text-lg text-ink-soft">Perimeter detection depends on details decided during design. These items need coordinating early:</p>
        <ul className="mt-6 flex flex-wrap gap-3">
          {COORD.map((c) => <li key={c} className="rounded-full border border-border px-4 py-2 text-sm">{c}</li>)}
        </ul>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/contact" className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">Discuss Perimeter Design Coordination <ArrowRight className="h-4 w-4" aria-hidden /></Link>
          <Link to="/for-architects" className="tap-target inline-flex items-center rounded-full border border-border px-6 py-3 text-sm font-medium">For Architects</Link>
        </div>
      </Section>

      <Section id="faq" title="Frequently asked questions" alt>
        <div className="max-w-3xl divide-y divide-border border-y border-border">
          {FAQS.map((f) => (
            <div key={f.q} className="py-6">
              <h3 className="text-lg font-semibold">{f.q}</h3>
              <p className="mt-2 text-ink-soft">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="related" title="Related expertise">
        <ul className="grid gap-3 sm:grid-cols-2 max-w-4xl">
          <li><Link to="/security-consultant-in-india" className="text-primary">Security Consultant in India</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "perimeter-protection" }} className="text-primary">Perimeter Protection</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "security-infrastructure" }} className="text-primary">Security Infrastructure</Link></li>
          <li><Link to="/projects" className="text-primary">Projects</Link></li>
          <li><Link to="/for-architects" className="text-primary">For Architects</Link></li>
          <li><Link to="/ask-arif/$slug" params={{ slug: "how-does-perimeter-security-work" }} className="text-primary">How does perimeter security work?</Link></li>
          <li><Link to="/ask-arif/$slug" params={{ slug: "mistakes-in-perimeter-security" }} className="text-primary">Mistakes in perimeter security projects</Link></li>
          <li><Link to="/ask-arif/$slug" params={{ slug: "perimeter-security-for-private-estates" }} className="text-primary">Perimeter security for private estates</Link></li>
        </ul>
      </Section>

      <section aria-labelledby="cta" className="py-24 border-t border-border bg-surface">
        <div className="container-page text-center max-w-3xl">
          <h2 id="cta" className="text-3xl md:text-4xl font-semibold tracking-tight">Planning security for a complex perimeter?</h2>
          <p className="mt-4 text-ink-soft">The right starting point is understanding the site, risk, boundary and response requirement, before deciding on sensors or brands. Email {SITE.email} or call {SITE.phone}.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">Discuss a Perimeter Security Project <ArrowRight className="h-4 w-4" aria-hidden /></Link>
            <Link to="/contact" className="tap-target inline-flex items-center rounded-full border border-border px-6 py-3 text-sm font-medium">Contact Arif Khan</Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
